import { AuditedRowSpec } from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

/**
 * Row model for the Provisional Financials statement view (RECUR-27786), built to match the
 * audited redesign's (RECUR-27010) statutory-waterfall structure so both statements read
 * identically wherever their categories overlap. Reuses `AuditedRowSpec` and joins by
 * `classificationName` (`src`) exactly like the audited layout — never `classificationId` —
 * since `AuditedStatementTable` only knows how to join by name.
 *
 * Differs from the audited P&L layout in exactly one place: CAC (classificationId 5) has its
 * own row here. CAC is a live, active provisional category (real data, feeds `Total Operating
 * Cost` via `effCAC`/`cacLegacy` in MisService.ts) but audited has zero CAC rows, so the shared
 * audited layout has no slot for it — provisional needs one.
 *
 * `Balance with Statutory Authorities` (classificationId 10) similarly gets its own row here,
 * even though the audited layout omits it — it already has a valid `aggregation` bucket
 * (`Total Current Assets`) on the backend, so this is a display-only addition.
 */

export const BALANCE_SHEET_LAYOUT: AuditedRowSpec[] = [
    { type: 'group', label: 'Equity and Liabilities', src: null },
    { type: 'sub', label: 'Equity', src: null },
    { type: 'item', label: 'Share Capital', src: 'Share Capital' },
    { type: 'item', label: 'Reserves and Surplus', src: 'Reserves and Surplus' },
    { type: 'item', label: 'Other Equity', src: 'Other Equity' },
    { type: 'subtotal', label: 'Total Equity', src: 'Total Shareholder Funds', rename: true },
    { type: 'sub', label: 'Liabilities', src: null },
    { type: 'sub', label: 'Non-current Liabilities', src: null },
    { type: 'item', label: 'Long Term Borrowings', src: 'Long Term Borrowings' },
    { type: 'item', label: 'Net Deferred Tax Liabilities', src: 'Net Deferred Tax Liabilities' },
    { type: 'item', label: 'Other Long Term Liabilities', src: 'Non Current Liabilities', rename: true },
    { type: 'item', label: 'Long Term Provisions', src: 'Long Term Provisions' },
    {
        type: 'subtotal',
        label: 'Total Non-current Liabilities',
        src: 'Total Non Current Liabilities',
        rename: true,
    },
    { type: 'sub', label: 'Current Liabilities', src: null },
    { type: 'item', label: 'Short Term Borrowings', src: 'Short Term Borrowings' },
    { type: 'item', label: 'Trade Payables', src: 'Trade Payables' },
    { type: 'item', label: 'Other Current Liabilities', src: 'Other Liabilities', rename: true },
    { type: 'item', label: 'Short Term Provisions', src: 'Short Term Provisions' },
    { type: 'subtotal', label: 'Total Current Liabilities', src: 'Total Current Liabilities' },
    { type: 'subtotal', label: 'Total Equity and Liabilities', src: 'Total Equities Liabilities', rename: true, grand: true },
    { type: 'group', label: 'Assets', src: null },
    { type: 'sub', label: 'Net Fixed Assets', src: null },
    { type: 'item', label: 'Tangible Assets', src: 'Tangible Assets' },
    { type: 'item', label: 'Intangible Assets', src: 'Intangible Assets' },
    { type: 'subtotal', label: 'Total Net Fixed Assets', src: 'Net Fixed Assets', rename: true },
    { type: 'item', label: 'Capital Work-in-progress', src: 'Capital Work-in-Progress', rename: true },
    { type: 'sub', label: 'Other Non-current Assets', src: null },
    { type: 'item', label: 'Non-current Investments', src: 'Investments', rename: true },
    { type: 'item', label: 'Net Deferred Tax Assets', src: 'Net Deferred Tax Assets' },
    { type: 'item', label: 'Long Term Loans and Advances', src: 'Long Term Loans and Advances' },
    { type: 'item', label: 'Other Non-current Assets', src: 'Non Current Assets', rename: true },
    {
        type: 'subtotal',
        label: 'Total Other Non-current Assets',
        src: 'Total Other Non-Current Assets',
        rename: true,
    },
    { type: 'sub', label: 'Current Assets', src: null },
    { type: 'item', label: 'Current Investments', src: 'Current Investments' },
    { type: 'item', label: 'Inventories', src: 'Inventories' },
    { type: 'item', label: 'Trade Receivables', src: 'Trade Receivables' },
    { type: 'item', label: 'Cash and Bank Balances', src: 'Cash And Cash Equivalents', rename: true },
    { type: 'item', label: 'Short Term Loans and Advances', src: 'Other Advances / Deposits', rename: true },
    // Provisional-only addition — see file header. Not present in the audited layout.
    { type: 'item', label: 'Balance with Statutory Authorities', src: 'Balance with Statutory Authorities' },
    { type: 'item', label: 'Other Current Assets', src: 'Other Assets', rename: true },
    { type: 'subtotal', label: 'Total Current Assets', src: 'Total Current Assets' },
    { type: 'subtotal', label: 'Total Assets', src: 'Total Assets', grand: true },
];

export const PROFIT_AND_LOSS_LAYOUT: AuditedRowSpec[] = [
    { type: 'subtotal', label: 'Net Revenue', src: 'Revenue', rename: true },
    { type: 'group', label: 'Operating Cost', src: null },
    // Provisional-only addition — see file header. Not present in the audited layout. Needs
    // MisService.ts's `totalOperatingCost` to add `+ cacLegacy` so this subtotal stays correct.
    { type: 'item', label: 'CAC', src: 'CAC' },
    { type: 'item', label: 'Cost of Materials Consumed', src: 'Cost of Materials Consumed' },
    { type: 'item', label: 'Purchases of Stock-in-trade', src: 'Purchases of Stock in Trade', rename: true },
    {
        type: 'item',
        label: 'Changes in Inventories / Finished Goods',
        src: 'Changes in Inventories / Finished Goods',
    },
    { type: 'item', label: 'Employee Benefit Expense', src: 'Employee Benefit Expense' },
    { type: 'item', label: 'Other Expenses', src: 'Other Expenses' },
    { type: 'subtotal', label: 'Total Operating Cost', src: 'Total Operating Cost' },
    { type: 'subtotal', label: 'Operating Profit (EBITDA)', src: 'Operating Profit (EBITDA)' },
    { type: 'item', label: 'Other Income', src: 'Other Income' },
    { type: 'item', label: 'Depreciation and Amortization Expense', src: 'Depreciation', rename: true },
    { type: 'subtotal', label: 'Profit Before Interest and Tax', src: 'Profit Before Interest and Tax' },
    { type: 'item', label: 'Finance Costs', src: 'Finance Costs' },
    {
        type: 'subtotal',
        label: 'Profit Before Tax and Exceptional Items Before Tax',
        src: 'Profit Before Tax and Exceptional Items',
        rename: true,
    },
    { type: 'item', label: 'Exceptional Items Before Tax', src: 'Exceptional Items Before Tax' },
    { type: 'subtotal', label: 'Profit Before Tax', src: 'Profit Before Tax' },
    { type: 'item', label: 'Income Tax', src: 'Tax', rename: true },
    {
        type: 'subtotal',
        label: 'Profit for the Period from Continuing Operations',
        src: 'Profit for Period from Continuing Operations',
        rename: true,
    },
    {
        type: 'item',
        label: 'Profit from Discontinuing Operations After Tax',
        src: 'Profit from Discontinuing Operation After Tax',
        rename: true,
    },
    {
        type: 'item',
        label: 'Minority Interest and Profit from Associates and Joint Ventures',
        src: 'Minority Interest and Profit from Associates and Joint Ventures',
    },
    { type: 'subtotal', label: 'Profit for the Period', src: 'Profit for Period', rename: true },
    // RECUR-28287: Other Comprehensive Income (Ind-AS). Code-computed derived rows — hidden
    // entirely (not a '—' row) for any document/period with no OCI data.
    { type: 'subtotal', label: 'Other Comprehensive Income', src: 'Other Comprehensive Income', hideWhenAbsent: true },
    { type: 'subtotal', label: 'Total Comprehensive Income', src: 'Total Comprehensive Income', hideWhenAbsent: true },
];

/**
 * Cash Flow — RECUR-27640.
 *
 * Starts from the audited layout because the audited cash-flow classification names are the only
 * verified set that exists. ⚠️ Still unverified against real provisional cash-flow data: provisional
 * carries categories audited never populates, which is exactly why the Balance Sheet and P&L layouts
 * above had to be forked (CAC, Balance with Statutory Authorities).
 *
 * Both join directions matter, and only one is harmless:
 *   - a layout `src` the payload lacks   → renders '—'. Harmless.
 *   - a payload row absent from the layout → would render NOWHERE. Silent omission of financial
 *     data on an underwriting surface, which is not acceptable.
 * `appendUnmatchedRows` below closes the second case for every fixed layout, so an unverified
 * layout degrades to "shown in an Other section" rather than "silently dropped".
 */
export { CASH_FLOW_LAYOUT } from '../../../AuditedFinancialsVisualisation/auditedStatementLayout';

/**
 * Appends any payload classification the fixed layout has no row for, so nothing the backend
 * returned can disappear (RECUR-27640).
 *
 * `AuditedStatementTable` renders `layout.map(...)` and looks each row up by `src` — rows are
 * driven entirely by the layout, so a classification the layout does not name is invisible with no
 * count, no residual bucket and no signal. Pre-RECUR-27640 provisional Cash Flow rendered through
 * ClassifiedTable, which showed whatever came back; this keeps that losslessness while gaining the
 * statutory ordering.
 *
 * Aggregated rows are skipped — a backend subtotal that the layout deliberately omits (e.g. the
 * intentionally-dropped `Total Liabilities`) is not missing data and must not reappear as an item.
 */
export const appendUnmatchedRows = (layout: AuditedRowSpec[], tableData: any): AuditedRowSpec[] => {
    const known = new Set(layout.map((row) => row.src).filter(Boolean));
    const unmatched = (tableData?.rows ?? []).filter(
        (row: any) => row?.classificationName && !row.isAggregated && !known.has(row.classificationName),
    );
    if (!unmatched.length) return layout;
    return [
        ...layout,
        { type: 'group', label: 'Other Classifications', src: null },
        ...unmatched.map((row: any) => ({
            type: 'item' as const,
            label: row.classificationName,
            src: row.classificationName,
        })),
    ];
};
